/**
 * AuctionTabsService.js
 * 
 * Service layer for the new /AuctionTabs API endpoints.
 * These endpoints support the BRD tab-based grid functionality:
 * - Auction Tab
 * - Bid Review Tab
 * - Sold Tab
 * - Pending Tab
 */

import axios from 'axios'
import BaseService from './BaseService'

const Environment = require('../environments/environment.json')

// ============================================================================
// TAB COUNTS
// ============================================================================

/**
 * Get VIN counts for all tabs and sub-statuses.
 * Use this to display badge counts on tabs.
 * 
 * @param {number|null} oemId - Optional OEM filter
 * @param {string} sessionKey - User session key
 * @returns {Promise} Response with tab counts
 */
const getTabCounts = (oemId, sessionKey) =>
{
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				const params = oemId ? `?oemId=${oemId}` : ''
				resolve(axios.get(`${Environment.apiUrl}/AuctionTabs/Counts${params}`, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

// ============================================================================
// SEARCH ENDPOINTS
// ============================================================================

/**
 * Search Auction Tab.
 * Returns paginated results for live auctions.
 * 
 * @param {Object} searchRequest - Search parameters
 * @param {string} sessionKey - User session key
 * @returns {Promise} Paginated auction results
 */
const searchAuctionTab = (searchRequest, sessionKey) =>
{
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Auction/Search`, searchRequest, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Search Bid Review Tab.
 * Returns auctions in Bid Review status awaiting seller decision.
 * 
 * @param {Object} searchRequest - Search parameters
 * @param {string} sessionKey - User session key
 * @returns {Promise} Paginated bid review results
 */
const searchBidReviewTab = (searchRequest, sessionKey) =>
{
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/BidReview/Search`, searchRequest, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Search Sold Tab.
 * Returns completed sales with buyer/sale details.
 * 
 * @param {Object} searchRequest - Search parameters (includes saleDateFrom, saleDateTo, isVoided)
 * @param {string} sessionKey - User session key
 * @returns {Promise} Paginated sold results
 */
const searchSoldTab = (searchRequest, sessionKey) =>
{
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Sold/Search`, searchRequest, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Search Pending Tab.
 * Returns auctions in pending states (Unsold, Shipping, Physical Auction, etc.).
 * 
 * @param {Object} searchRequest - Search parameters (includes pendingStatus)
 * @param {string} sessionKey - User session key
 * @returns {Promise} Paginated pending results
 */
const searchPendingTab = (searchRequest, sessionKey) =>
{
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Pending/Search`, searchRequest, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

// ============================================================================
// DETAIL ENDPOINTS (Popups)
// ============================================================================

/**
 * Get Bid Details for an auction.
 * Returns all bids with bidder info for the bid details popup.
 * 
 * @param {number} auctionId - Auction ID
 * @param {string} sessionKey - User session key
 * @returns {Promise} Bid details with history
 */
const getBidDetails = (auctionId, sessionKey) =>
{
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.get(`${Environment.apiUrl}/AuctionTabs/BidDetails/${auctionId}`, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Get Pricing History for an auction.
 * Returns pricing and status change history.
 * 
 * @param {number} auctionId - Auction ID
 * @param {string} sessionKey - User session key
 * @returns {Promise} Pricing history entries
 */
const getPricingHistory = (auctionId, sessionKey) =>
{
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.get(`${Environment.apiUrl}/AuctionTabs/PricingHistory/${auctionId}`, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

// ============================================================================
// AUCTION TAB ACTIONS
// ============================================================================

/**
 * Place an auction on hold.
 * 
 * @param {number} auctionId - Auction ID
 * @param {number} holdReasonId - Hold reason ID (see AuctionHoldReasonTypes enum)
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const placeOnHold = (auctionId, holdReasonId, notes, sessionKey) =>
{
	const request = {
		auctionId,
		holdReasonId,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Hold`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Release an auction from hold.
 * 
 * @param {number} auctionId - Auction ID
 * @param {string} releaseNotes - Notes for the release
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const releaseFromHold = (auctionId, releaseNotes, sessionKey) =>
{
	const request = {
		auctionId,
		releaseNotes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/ReleaseHold`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Release an auction from "Awaiting Seller Release" to Live Auction.
 * BRD: "Ability to release units into live auction (individually or bulk release)"
 * 
 * @param {number} auctionId - Auction ID
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const releaseToAuction = (auctionId, sessionKey) =>
{
	const request = {
		auctionId
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/ReleaseToAuction`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Bulk release multiple auctions from "Awaiting Seller Release" to Live Auction.
 * BRD: "Ability to release units into live auction (individually or bulk release)"
 * 
 * @param {number[]} auctionIds - Array of Auction IDs to release
 * @param {string} sessionKey - User session key
 * @returns {Promise} Response with results for each auction
 */
const bulkReleaseToAuction = (auctionIds, sessionKey) =>
{
	const request = {
		auctionIds
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/BulkReleaseToAuction`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Update pricing for an auction.
 * 
 * @param {number} auctionId - Auction ID
 * @param {number} mbpPrice - MBP price
 * @param {number} startPrice - Start price
 * @param {number} reservePrice - Reserve price
 * @param {number} buyNowPrice - Buy Now price
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const updatePricing = (auctionId, mbpPrice, startPrice, reservePrice, buyNowPrice, marketPrice, notes, sessionKey) =>
{
	const request = {
		auctionId,
		mbpPrice,
		startPrice,
		reservePrice,
		buyNowPrice,
		marketPrice,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/UpdatePricing`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Change auction iteration (MBP → IT2 → IT3 → IT4 → OpenLane).
 * 
 * @param {number} auctionId - Auction ID
 * @param {number} targetIterationId - Target iteration ID (see AuctionIterationTypes enum)
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const changeIteration = (auctionId, targetIterationId, notes, sessionKey) =>
{
	const request = {
		auctionId,
		targetIterationId,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/ChangeIteration`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

// ============================================================================
// BID REVIEW TAB ACTIONS
// ============================================================================

/**
 * Send a counter offer to the high bidder.
 * 
 * @param {number} auctionId - Auction ID
 * @param {number} auctionTierMemberBidId - High bid ID
 * @param {number} counterAmount - Counter offer amount
 * @param {number} expiryHours - Hours until counter expires
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} New AuctionCounterOfferId
 */
const sendCounterOffer = (auctionId, auctionTierMemberBidId, counterAmount, expiryHours, notes, sessionKey) =>
{
	const request = {
		auctionId,
		auctionTierMemberBidId,
		counterAmount,
		expiryHours,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/BidReview/Counter`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Accept the current high bid and complete the sale.
 * 
 * @param {number} auctionId - Auction ID
 * @param {number} auctionTierMemberBidId - High bid ID to accept
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const sellAtHighBid = (auctionId, auctionTierMemberBidId, notes, sessionKey) =>
{
	const request = {
		auctionId,
		auctionTierMemberBidId,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/BidReview/Sell`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

// ============================================================================
// SOLD TAB ACTIONS
// ============================================================================

/**
 * Void a completed sale.
 * 
 * @param {number} auctionId - Auction ID
 * @param {string} voidReason - Reason for voiding
 * @param {boolean} notifyAccounting - Whether to notify accounting
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const voidSale = (auctionId, voidReason, notifyAccounting, sessionKey) =>
{
	const request = {
		auctionId,
		voidReason,
		notifyAccounting
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Sold/Void`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

// ============================================================================
// PENDING TAB ACTIONS
// ============================================================================

/**
 * Relist an unsold auction.
 * 
 * @param {number} auctionId - Auction ID
 * @param {number} targetIterationId - Target iteration ID
 * @param {number} newStartPrice - New start price
 * @param {number} newReservePrice - New reserve price
 * @param {number} newBuyNowPrice - New buy now price
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const relistAuction = (auctionId, targetIterationId, newStartPrice, newReservePrice, newBuyNowPrice, notes, sessionKey) =>
{
	const request = {
		auctionId,
		targetIterationType: targetIterationId, // Must match RelistAuctionRequestDto.TargetIterationType
		newStartPrice,
		newReservePrice,
		newBuyNowPrice,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Relist`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Update auction status for pending tab transitions.
 * 
 * @param {number} auctionId - Auction ID
 * @param {number} newStatusId - New status ID (see AuctionTabStatuses enum)
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const updatePendingStatus = (auctionId, newStatusId, notes, sessionKey) =>
{
	const request = {
		auctionId,
		newStatusId,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Pending/UpdateStatus`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Move an unsold auction to Shipping status.
 * BRD: Manual action from Unsold state.
 * 
 * @param {number} auctionId - Auction ID
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const moveToShipping = (auctionId, notes, sessionKey) =>
{
	const request = {
		auctionId,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Pending/MoveToShipping`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Move a vehicle from Shipping to Physical Auction.
 * BRD: Check in to physical auction lane.
 * 
 * @param {number} auctionId - Auction ID
 * @param {string} notes - Optional notes
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const moveToPhysicalAuction = (auctionId, notes, sessionKey) =>
{
	const request = {
		auctionId,
		notes
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Pending/MoveToPhysicalAuction`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

/**
 * Unground a vehicle (dealer requests vehicle back).
 * BRD: Dealer unground request.
 * 
 * @param {number} auctionId - Auction ID
 * @param {string} reason - Reason for ungrounding
 * @param {string} sessionKey - User session key
 * @returns {Promise} Success/failure
 */
const ungroundVehicle = (auctionId, reason, sessionKey) =>
{
	const request = {
		auctionId,
		reason
	}
	
	return new Promise((resolve, reject) =>
	{
		BaseService
			.requestHeaders(sessionKey)
			.then((headers) =>
			{
				resolve(axios.post(`${Environment.apiUrl}/AuctionTabs/Pending/Unground`, request, headers))
			})
			.catch((error) =>
			{
				reject(error)
			})
	})
}

// ============================================================================
// SERVICE EXPORT
// ============================================================================

const AuctionTabsService = {
	// Counts
	getTabCounts,
	
	// Search
	searchAuctionTab,
	searchBidReviewTab,
	searchSoldTab,
	searchPendingTab,
	
	// Details (Popups)
	getBidDetails,
	getPricingHistory,
	
	// Auction Tab Actions
	placeOnHold,
	releaseFromHold,
	releaseToAuction,
	bulkReleaseToAuction,
	updatePricing,
	changeIteration,
	
	// Bid Review Tab Actions
	sendCounterOffer,
	sellAtHighBid,
	
	// Sold Tab Actions
	voidSale,
	
	// Pending Tab Actions
	relistAuction,
	updatePendingStatus,
	moveToShipping,
	moveToPhysicalAuction,
	ungroundVehicle,
}

export default AuctionTabsService
